class PredictiveSearch extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector('input[type="search"]')
    this.form = this.querySelector('form')
    this.predictiveSearchResults = this.querySelector('#predictive-search')

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event)
    }, 300).bind(this))
    this.form.addEventListener('submit', (event) => {
      this.onSubmit(event)
    })
  }

  onChange() {
    const searchTerm = this.input.value.trim()

    if (!searchTerm.length) {
      this.close()
      return
    }

    this.getSearchResults(searchTerm)
  }

  onSubmit(e) {
    e.preventDefault()
    const searchTerm = this.input.value.trim()
    if (!searchTerm.length) {
      this.form.submit()
      return
    }
    window.location.href = this.resultsUrl(searchTerm)
  }

  // The results page is asked for the exact search first (see exactTerm): Shopify's loose search adds
  // near matches (« moreau naudet » brings Morey wines), which crowd the list and its filters. When the
  // lists answer has already shown that the exact search finds nothing (typo, another spelling, a word
  // still being typed), the link goes straight to the loose search; otherwise the results page falls
  // back on its own (snippets/search-exact-fallback.liquid).
  resultsUrl(searchTerm) {
    const nothingExact = this.exact && this.exact.term === searchTerm && !this.exact.found
    const terms = nothingExact ? searchTerm : PredictiveSearch.exactTerm(searchTerm)
    return '/search?q=' + encodeURIComponent(terms) + '&type=product&options%5Bprefix%5D=last'
  }

  // Exact search: each word in quotes must be found as written (accents and case aside). A word that
  // can follow an elided article also stands for « d'… » and « l'… », which the products write as one
  // word (« yquem » finds « Château d'Yquem »).
  static exactTerm(searchTerm) {
    return searchTerm
      .replace(/’/g, "'")
      .replace(/["()]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => {
        if (word.length > 2 && /^[aeiouyhàâäéèêëîïôöùûü]/i.test(word) && !word.includes("'")) return `("${word}" OR "d'${word}" OR "l'${word}")`
        return `"${word}"`
      })
      .join(' ')
  }

  // The panel is rendered twice: in Shopify's default order (wines in stock first, sold-out ones only in
  // the places left) and by relevance alone. The second render brings back a sold-out wine searched by
  // its name, which the first one drops as soon as 10 wines in stock match the search loosely.
  // The lists of the whole search come in a third, slower answer and never hold the wines back.
  getSearchResults(searchTerm) {
    this.request = (this.request || 0) + 1
    const request = this.request
    const matches = this.matcher(searchTerm)
    const lists = matches ? this.fetchLists(searchTerm).catch(() => null) : Promise.resolve(null)

    Promise.allSettled([this.fetchPanel(searchTerm, 'last'), this.fetchPanel(searchTerm, 'show')])
      .then(([inStockFirst, byRelevance]) => {
        // A newer search has been typed since: this answer is stale.
        if (request !== this.request) return
        if (inStockFirst.status !== 'fulfilled' || !inStockFirst.value) {
          this.close()
          return
        }

        const panel = inStockFirst.value
        if (byRelevance.status === 'fulfilled' && byRelevance.value) this.mergePanels(panel, byRelevance.value, matches)
        this.predictiveSearchResults.innerHTML = panel.innerHTML
        this.open()
        this.setSeeAllLinks(searchTerm)

        lists.then((index) => {
          if (!index || request !== this.request) return
          const counter = index.querySelector('[data-results-count]')
          if (counter) this.exact = { term: searchTerm, found: Number(counter.dataset.resultsCount) > 0 }
          this.setSeeAllLinks(searchTerm)
          this.completeLists(index, matches)
        })
      })
  }

  setSeeAllLinks(searchTerm) {
    this.predictiveSearchResults.querySelectorAll('.predictive_see_all').forEach((link) => {
      link.setAttribute('href', this.resultsUrl(searchTerm))
    })
  }

  // resources[type]=product: without it the 10 places are shared with collections, pages and query
  // suggestions, which the panel does not show.
  fetchPanel(searchTerm, unavailableProducts) {
    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': 10,
      'resources[options][unavailable_products]': unavailableProducts,
      section_id: 'predictive-search'
    })

    return this.fetchSection(`/search/suggest?${params}`, 'predictive-search')
  }

  // sections/predictive-lists.liquid: the appellations of every result, and the Sélection and Domaine
  // entries of the 50 most relevant wines, sold-out ones included.
  // Asked as an exact search (see exactTerm). Left loose, Shopify also returns near matches (far more of
  // them for a visitor with cookies), and its appellation filter, capped at 100 values, drops the small
  // appellations (Petit Chablis on « chablis »). A word still being typed finds nothing: the lists then
  // stay built from the wines shown.
  fetchLists(searchTerm) {
    const params = new URLSearchParams({
      q: PredictiveSearch.exactTerm(searchTerm),
      type: 'product',
      'options[unavailable_products]': 'show',
      section_id: 'predictive-lists'
    })

    return this.fetchSection(`/search?${params}`, 'predictive-lists')
  }

  fetchSection(url, sectionId) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status)
        return response.text()
      })
      .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector(`#shopify-section-${sectionId}`))
  }

  // Returns a test "does this text match every word typed?": each word of 3 letters or more must start
  // a word of the text. Null when nothing typed is long enough to tell.
  matcher(searchTerm) {
    const words = this.normalize(searchTerm).split(' ').filter((word) => word.length > 2)
    if (words.length === 0) return null

    return (text) => {
      const normalized = ' ' + this.normalize(text)
      return words.every((word) => normalized.includes(' ' + word))
    }
  }

  // Keeps 10 wines out of the two renders: those matching every word typed first, wines in stock before
  // sold-out ones at equal match, Shopify's order otherwise. The three lists then follow the wines kept.
  mergePanels(panel, other, matches) {
    const wrapper = panel.querySelector('.swiper-wrapper')
    if (!wrapper) return

    const seen = new Set()
    const items = []
    ;[panel, other].forEach((render) => {
      render.querySelectorAll('.predictive-search__list-item').forEach((item) => {
        if (seen.has(item.dataset.handle)) return
        seen.add(item.dataset.handle)
        const matchesEveryWord = !matches || matches(item.dataset.searchText)
        items.push({ item, position: items.length, rank: (matchesEveryWord ? 0 : 2) + (item.dataset.available === 'true' ? 0 : 1) })
      })
    })
    // No wine at all: the panel keeps its "no result" state.
    if (items.length === 0) return

    const shown = items.sort((a, b) => a.rank - b.rank || a.position - b.position).slice(0, 10).map((entry) => entry.item)
    wrapper.replaceChildren(...shown)
    panel.querySelector('[data-products-count]').textContent = shown.length

    PredictiveSearch.LISTS.forEach((kind) => {
      const entries = new Map()
      ;[panel, other].forEach((render) => {
        render.querySelectorAll(`[data-suggest-block="${kind}"] li`).forEach((entry) => {
          if (!entries.has(entry.dataset.suggest)) entries.set(entry.dataset.suggest, entry)
        })
      })
      const kept = []
      shown.forEach((item) => {
        const entry = entries.get(item.dataset[kind])
        if (entry && !kept.includes(entry)) kept.push(entry)
      })
      this.fillList(panel, kind, kept)
    })
  }

  // Lists built from 10 wines miss what those wines do not carry. Out of the lists of the whole search,
  // the entries matching every word typed take over, the fullest first: appellations and domaines by
  // their own name, or else those of the matching selections. A list with no such entry stays as it is.
  completeLists(index, matches) {
    const collect = (kind) => {
      const entries = new Map()
      index.querySelectorAll(`[data-suggest-block="${kind}"] li`).forEach((node) => {
        const entry = entries.get(node.dataset.suggest)
        if (entry) entry.count += 1
        else entries.set(node.dataset.suggest, { node, count: Number(node.dataset.count) || 1 })
      })
      return Array.from(entries.values())
    }

    const selections = collect('selection').filter((entry) => matches(entry.node.dataset.suggest))
    const named = (kind) => {
      const entries = collect(kind)
      const byName = entries.filter((entry) => matches(entry.node.dataset.suggest))
      if (byName.length > 0) return byName
      const carried = new Set(selections.map((entry) => entry.node.dataset[kind]))
      return entries.filter((entry) => carried.has(entry.node.dataset.suggest))
    }

    const lists = { selection: selections, appellation: named('appellation'), domaine: named('domaine') }
    PredictiveSearch.LISTS.forEach((kind) => {
      if (lists[kind].length === 0) return
      const kept = lists[kind].sort((a, b) => b.count - a.count).map((entry) => entry.node)
      this.fillList(this.predictiveSearchResults, kind, kept)
    })
  }

  // Lists are cut short: under 1024px they sit above the wines and push them down.
  fillList(panel, kind, allEntries) {
    const block = panel.querySelector(`[data-suggest-block="${kind}"]`)
    if (!block) return

    const limit = window.matchMedia('(min-width: 1024px)').matches ? PredictiveSearch.LIST_LIMIT.desktop : PredictiveSearch.LIST_LIMIT.mobile
    const entries = allEntries.slice(0, limit)

    block.querySelector('ul').replaceChildren(...entries)
    block.querySelector('[data-suggest-count]').textContent = entries.length
    const empty = block.querySelector('[data-suggest-empty]')
    if (empty) empty.hidden = entries.length > 0
    else block.hidden = entries.length === 0
  }

  // Lowercase, no accents, words separated by single spaces.
  normalize(text) {
    return (text || '')
      .toLowerCase()
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    const swiper = new Swiper('#predictive-search-results-list .swiper', {
      navigation: {
        prevEl: '#predictive-search-results-nav button:first-of-type',
        nextEl: '#predictive-search-results-nav button:last-of-type',
      },
      breakpoints: {
        0: {
          slidesPerView: 2.3,
          spaceBetween: 16
        },
        1280: {
          slidesPerView: 3,
          spaceBetween: 40
        }
      }
    })
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

PredictiveSearch.LISTS = ['selection', 'appellation', 'domaine']
PredictiveSearch.LIST_LIMIT = { desktop: 8, mobile: 5 }

customElements.define('predictive-search', PredictiveSearch);
